import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
    Box,
    Typography,
    Avatar,
    Grid,
    IconButton,
    Container,
    Stack,
    Button,
    useMediaQuery,
    useTheme,
    Fade,
    Grow,
    Paper,
    Divider,
    SvgIcon,
    TextField,
    Select,
    MenuItem,
    FormControl,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import {
    Phone,
    Email,
    Language,
    LinkedIn,
    GitHub,
    Instagram,
    WhatsApp,
    Facebook,
    Business,
    LocationOn,
    Download,
    Share,
    Person,
    QrCode2,
    Info,
    Work,
    PhotoLibrary,
    PlayArrow,
    EmojiEvents,
    PictureAsPdf,
    VideoLibrary,
    YouTube
} from '@mui/icons-material';
import config from '../../../config';
import { saveContactToDevice } from '../../../utils/vCardUtils.js';
import { trackCardInteraction } from '../../../api/analyticsApi';
import { getFullUrl } from '../../../utils/urlUtils';
import CardShare from '../CardShare';

// Custom X (former Twitter) Icon
const XIcon = (props) => (
    <SvgIcon {...props} viewBox="0 0 24 24">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
    </SvgIcon>
);

// Social media icons mapping
const socialIcons = {
    linkedin: <LinkedIn />,
    github: <GitHub />,
    twitter: <Box sx={{ 
        backgroundColor: '#000', 
        borderRadius: '8px', 
        width: '24px', 
        height: '24px', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center'
    }}>
        <XIcon sx={{ color: '#fff', fontSize: '16px' }} />
    </Box>,
    x: <Box sx={{ 
        backgroundColor: '#000', 
        borderRadius: '8px', 
        width: '24px', 
        height: '24px', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center'
    }}>
        <XIcon sx={{ color: '#fff', fontSize: '16px' }} />
    </Box>,
    instagram: <Instagram />,
    facebook: <Facebook />,
    whatsapp: <WhatsApp />,
    website: <Language />,
};

// Contact type icons
const getContactIcon = (type) => {
    switch (type?.toLowerCase()) {
        case 'email':
            return <Email />;
        case 'phone':
            return <Phone />;
        case 'whatsapp':
            return <WhatsApp />;
        default:
            return <Business />;
    }
};

// Contact type colors and backgrounds
const getContactStyle = (type) => {
    switch (type?.toLowerCase()) {
        case 'email':
            return { color: '#ffffff', bg: '#1976d2' };
        case 'phone':
            return { color: '#ffffff', bg: '#4caf50' };
        case 'whatsapp':
            return { color: '#ffffff', bg: '#25d366' };
        default:
            return { color: '#ffffff', bg: '#2196f3' };
    }
};

// Social platform colors
const getSocialStyle = (platform) => {
    switch (platform?.toLowerCase()) {
        case 'linkedin':
            return { color: '#ffffff', bg: '#0077b5' };
        case 'github':
            return { color: '#ffffff', bg: '#333333' };
        case 'twitter':
        case 'x':
            return { color: '#ffffff', bg: '#000000' };
        case 'instagram':
            return { color: '#ffffff', bg: '#e4405f' };
        case 'facebook':
            return { color: '#ffffff', bg: '#1877f2' };
        case 'whatsapp':
            return { color: '#ffffff', bg: '#25d366' };
        default:
            return { color: '#ffffff', bg: '#6366f1' };
    }
};

// Helper function to get contact links
const getContactLink = (contact, cardHolderName) => {
    const { type, value, country } = contact;
    
    if (type === 'email') {
        return `mailto:${value}`;
    } else if (type === 'phone') {
        const prefix = country === 'SG' ? '+65' : '+91';
        return `tel:${prefix}${value}`;
    } else if (type === 'whatsapp') {
        const prefix = country === 'SG' ? '65' : '91';
        const defaultMessage = encodeURIComponent(`Hello ${cardHolderName || 'there'}, How are you? Shall we talk about your services?`);
        return `https://wa.me/${prefix}${value}?text=${defaultMessage}`;
    }
    return '#';
};

// Helper function to format contact display
const getContactDisplay = (contact) => {
    const { type, value, country } = contact;
    
    if (type === 'email') {
        return value;
    } else if (type === 'phone' || type === 'whatsapp') {
        const prefix = country === 'SG' ? '+65' : '+91';
        return `${prefix} ${value}`;
    }
    return value;
};

// Helper function to get image URL - handles both File objects and URLs
const getImageUrl = (value) => {
    if (!value) return null;
    if (value instanceof File) return URL.createObjectURL(value);
    if (typeof value === 'string' && (value.startsWith('http') || value.startsWith('blob'))) return value;
    if (typeof value === 'string') return getFullUrl(value);
    return null;
};

// Styled components for mobile-first design
const MobileContainer = styled(Box)(({ theme }) => ({
    minHeight: '100vh',
    background: '#ffffff',
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '100%',
    margin: '0 auto',
    [theme.breakpoints.up('sm')]: {
        maxWidth: '768px',
    },
    '& .main-content': {
        flex: 1,
        paddingBottom: theme.spacing(12), // 96px for desktop
        [theme.breakpoints.down('sm')]: {
            paddingBottom: theme.spacing(10), // 80px for mobile
        }
    }
}));

// Cover Photo Container
const CoverPhotoContainer = styled(Box)(({ theme }) => ({
    position: 'relative',
    height: '250px',
    width: '100%',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    overflow: 'hidden',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.3)',
        zIndex: 1,
    },
    [theme.breakpoints.up('md')]: {
        height: '300px',
    },
}));

// Company Logo Overlay
const CompanyLogoOverlay = styled(Box)(({ theme }) => ({
    position: 'absolute',
    top: '20px',
    right: '20px',
    zIndex: 3,
    background: 'rgba(255, 255, 255, 0.95)',
    padding: theme.spacing(1.5),
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
    backdropFilter: 'blur(10px)',
    [theme.breakpoints.up('md')]: {
        top: '30px',
        right: '30px',
        padding: theme.spacing(2),
    },
}));

const ProfileCard = styled(Box)(({ theme, hasProfilePicture }) => ({
    background: '#ffffff',
    borderRadius: '0',
    padding: theme.spacing(hasProfilePicture ? 0 : 2, 3, 2),
    textAlign: 'center',
    position: 'relative',
    zIndex: 1,
    marginTop: hasProfilePicture ? '1px' : '0',
    minHeight: 'auto',
    [theme.breakpoints.up('md')]: {
        padding: theme.spacing(hasProfilePicture ? 0 : 3, 4, 2),
        marginTop: hasProfilePicture ? '24px' : '0',
    },
}));

const ProfileAvatar = styled(Avatar)(({ theme }) => ({
    width: 120,
    height: 120,
    margin: '-15px auto 24px',
    border: '6px solid #ffffff',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
    backgroundColor: '#f0f4ff',
    position: 'relative',
    zIndex: 2,
    [theme.breakpoints.up('md')]: {
        width: 140,
        height: 140,
        margin: '0 auto 32px',
    },
    '@media (max-width: 400px)': {
        width: 100,
        height: 100,
        margin: '-10px auto 20px',
        border: '4px solid #ffffff',
    },
}));

// Content Section
const ContentSection = styled(Paper)(({ theme, hasProfilePicture }) => ({
    margin: theme.spacing(3, 2),
    padding: theme.spacing(3),
    borderRadius: '16px',
    background: '#ffffff',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    border: '1px solid #f0f0f0',
    [theme.breakpoints.up('md')]: {
        margin: theme.spacing(4, 3),
        padding: theme.spacing(4),
    },
    [theme.breakpoints.down('sm')]: {
        margin: theme.spacing(hasProfilePicture ? 2 : 1.5, 1),
        padding: theme.spacing(2),
        borderRadius: '12px',
    },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
    fontWeight: 700,
    color: '#1a202c',
    marginBottom: theme.spacing(2),
    fontSize: '1.5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    textAlign: 'center',
    '&::before': {
        content: '""',
        flex: 1,
        height: '2px',
        background: 'linear-gradient(90deg, #667eea, #764ba2)',
        marginRight: theme.spacing(2),
    },
    '&::after': {
        content: '""',
        flex: 1,
        height: '2px',
        background: 'linear-gradient(90deg, #667eea, #764ba2)',
        marginLeft: theme.spacing(2),
    },
    // Preview mode adjustments
    '@media (max-width: 400px)': {
        fontSize: '1.2rem',
        marginBottom: theme.spacing(1.5),
        '&::before, &::after': {
            flex: 0.5,
        },
    },
}));

const ActionGrid = styled(Grid)(({ theme, hasProfilePicture }) => ({
    marginTop: hasProfilePicture ? theme.spacing(3) : theme.spacing(2),
    padding: theme.spacing(0, 1),
    [theme.breakpoints.down('sm')]: {
        marginTop: hasProfilePicture ? theme.spacing(2) : theme.spacing(1),
    }
}));

const ActionButton = styled(IconButton)(({ theme }) => ({
    width: 56,
    height: 56,
    borderRadius: '50%', // Circle shape for all buttons
    margin: theme.spacing(0.5),
    border: '2px solid transparent',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    overflow: 'hidden',
    '&:hover': {
        transform: 'translateY(-2px) scale(1.03)',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
        borderColor: 'rgba(255, 255, 255, 0.3)',
    },
    '&:active': {
        transform: 'translateY(-1px) scale(1.01)',
    },
    '& .MuiSvgIcon-root': {
        fontSize: '1.4rem',
    },
    [theme.breakpoints.up('md')]: {
        width: 60,
        height: 60,
        '& .MuiSvgIcon-root': {
            fontSize: '1.5rem',
        },
    },
}));



const MainActionButton = styled(Button)(({ theme }) => ({
    borderRadius: '16px',
    padding: theme.spacing(1.5, 4),
    fontSize: '1rem',
    fontWeight: 600,
    textTransform: 'none',
    boxShadow: '0 6px 24px rgba(0, 0, 0, 0.15)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
    },
}));

const FloatingActionButton = styled(Button)(({ theme }) => ({
    background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
    color: '#fff',
    padding: '10px 20px',
    borderRadius: '50px',
    boxShadow: '0 4px 12px rgba(99, 102, 241, 0.25)',
    textTransform: 'none',
    fontWeight: 600,
    '&:hover': {
        background: 'linear-gradient(135deg, #4f46e5, #4338ca)',
        boxShadow: '0 6px 16px rgba(99, 102, 241, 0.35)',
        transform: 'translateY(-2px)',
    },
}));

const WhatsAppButton = styled(Button)(({ theme }) => ({
    background: 'linear-gradient(135deg, #25D366, #128C7E)',
    color: '#fff',
    padding: '10px 20px',
    borderRadius: '50px',
    boxShadow: '0 4px 12px rgba(37, 211, 102, 0.25)',
    textTransform: 'none',
    fontWeight: 600,
    '&:hover': {
        background: 'linear-gradient(135deg, #128C7E, #075E54)',
        boxShadow: '0 6px 16px rgba(37, 211, 102, 0.35)',
        transform: 'translateY(-2px)',
    },
}));

const WhatsAppForm = styled(Box)(({ theme }) => ({
    position: 'fixed',
    bottom: { xs: 80, sm: 100 },
    left: '50%',
    transform: 'translateX(-50%)',
    background: '#fff',
    padding: '12px',
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
    zIndex: 1000,
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    width: '90%',
    maxWidth: '400px',
    transition: 'all 0.3s ease',
}));

const PhoneInput = styled(TextField)({
    '& .MuiOutlinedInput-root': {
        borderRadius: '8px',
        '&:hover fieldset': {
            borderColor: '#25D366',
        },
        '&.Mui-focused fieldset': {
            borderColor: '#128C7E',
        },
    },
});

const CountrySelect = styled(Select)({
    borderRadius: '8px',
    minWidth: '100px',
    '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#25D366',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#128C7E',
    },
});

const InfoChip = styled(Box)(({ theme }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 2),
    borderRadius: '20px',
    backgroundColor: 'rgba(103, 126, 234, 0.1)',
    margin: theme.spacing(0.5),
    fontSize: '0.875rem',
    color: '#4c63d2',
}));

// Achievement styled components
const AchievementContainer = styled(Box)(({ theme }) => ({
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    padding: '16px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    border: '1px solid #e9ecef',
    transition: 'all 0.3s ease',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 16px rgba(0,0,0,0.12)'
    }
}));

const AchievementImageWrapper = styled(Box)({
    width: '100%',
    marginBottom: '12px'
});

const AchievementCaption = styled(Typography)({
    fontSize: '16px',
    fontWeight: 700,
    color: '#1a1a1a',
    margin: '0 0 20px 0',
    textAlign: 'center',
    lineHeight: 1.3,
    letterSpacing: '0.5px',
    borderBottom: '2px solid #e9ecef',
    paddingBottom: '8px',
    '@media (max-width: 768px)': {
        fontSize: '15px',
        margin: '0 0 16px 0',
        paddingBottom: '6px'
    }
});

const AchievementLinks = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginBottom: '12px'
});

const AchievementButton = styled(Button)({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '10px 16px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 600,
    textDecoration: 'none',
    transition: 'all 0.3s ease',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    textTransform: 'none',
    '& .achievement-icon': {
        fontSize: '18px'
    },
    '&.certificate': {
        color: 'white',
        background: 'linear-gradient(135deg, #4CAF50, #45a049)',
        '&:hover': {
            background: 'linear-gradient(135deg, #45a049, #3d8b40)',
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 8px rgba(76, 175, 80, 0.3)'
        }
    },
    '&.pdf': {
        color: 'white',
        background: 'linear-gradient(135deg, #2196F3, #1976D2)',
        '&:hover': {
            background: 'linear-gradient(135deg, #1976D2, #1565C0)',
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 8px rgba(33, 150, 243, 0.3)'
        }
    },
    '&.video': {
        color: 'white',
        background: 'linear-gradient(135deg, #FF9800, #F57C00)',
        '&:hover': {
            background: 'linear-gradient(135deg, #F57C00, #E65100)',
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 8px rgba(255, 152, 0, 0.3)'
        }
    }
});

const MediaContainer = styled(Box)({
    marginBottom: '12px',
    '&:last-of-type': {
        marginBottom: 0
    }
});

// Video container for YouTube embeds
const VideoContainer = styled(Box)({
    position: 'relative',
    width: '100%',
    height: '180px',
    borderRadius: '8px',
    overflow: 'hidden',
    background: '#000',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    '@media (max-width: 768px)': {
        height: '140px'
    }
});

const VideoOverlay = styled(Box)({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(0, 0, 0, 0.3)',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    '&:hover': {
        background: 'rgba(0, 0, 0, 0.5)',
        '& .play-icon': {
            transform: 'scale(1.1)',
            opacity: 1
        }
    }
});

const YouTubeEmbed = styled('iframe')({
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    border: 'none',
    borderRadius: '8px'
});

const UploadedVideoPlayer = styled('video')({
    width: '100%',
    height: '180px',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    objectFit: 'cover',
    '@media (max-width: 768px)': {
        height: '140px'
    }
});

const AchievementImage = styled('img')({
    width: '95px',
    height: '80px',
    objectFit: 'contain',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    background: '#f8f9fa',
    padding: '8px',
    margin: '0 auto',
    display: 'block',
    '@media (max-width: 768px)': {
        height: '70px',
        padding: '6px',
        width: '85px'
    }
});

const CorporateTheme = ({ card, isPreview, isEditMode }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [shareOpen, setShareOpen] = useState(false);
    const [playingVideos, setPlayingVideos] = useState({});
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [countryCode, setCountryCode] = useState('+65');
    const [error, setError] = useState('');

    const toggleVideo = (achievementId) => {
        setPlayingVideos(prev => ({
            ...prev,
            [achievementId]: !prev[achievementId]
        }));
    };

    // Helper function to extract YouTube video ID and create embed URL
    const getYouTubeVideoId = (url) => {
        if (!url) return null;
        const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
        const match = url.match(regExp);
        return (match && match[7].length === 11) ? match[7] : null;
    };

    const getYouTubeEmbedUrl = (url) => {
        const videoId = getYouTubeVideoId(url);
        if (!videoId) return null;
        return `https://www.youtube.com/embed/${videoId}?enablejsapi=1&autoplay=1&rel=0&modestbranding=1&fs=1&cc_load_policy=0&iv_load_policy=3&autohide=0&showinfo=1`;
    };

    // Helper function to check if a URL is an image
    const isImageFile = (url) => {
        if (!url) return false;
        const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'];
        const urlLower = url.toLowerCase();
        return imageExtensions.some(ext => urlLower.includes(ext));
    };

    // Helper function to check if a URL is a video file
    const isVideoFile = (url) => {
        if (!url) return false;
        const videoExtensions = ['.mp4', '.avi', '.mov', '.wmv', '.flv', '.webm', '.mkv', '.m4v'];
        const urlLower = url.toLowerCase();
        return videoExtensions.some(ext => urlLower.includes(ext));
    };

    // Helper function to get file extension
    const getFileExtension = (url) => {
        if (!url) return '';
        const urlLower = url.toLowerCase();
        if (urlLower.includes('.pdf')) return 'pdf';
        if (urlLower.includes('.doc') || urlLower.includes('.docx')) return 'doc';
        if (urlLower.includes('.mp4') || urlLower.includes('.avi') || urlLower.includes('.mov') || urlLower.includes('.wmv') || urlLower.includes('.webm')) return 'video';
        if (urlLower.includes('.jpg') || urlLower.includes('.jpeg') || urlLower.includes('.png') || urlLower.includes('.gif') || urlLower.includes('.bmp') || urlLower.includes('.webp')) return 'image';
        return 'unknown';
    };

    // Handle contact click
    const handleContactClick = async (contact) => {
        const link = getContactLink(contact, card.name);
        if (link !== '#') {
            await trackCardInteraction(card.slug, 'contact_click', contact.type);
            window.open(link, '_blank');
        }
    };

    // Handle social click
    const handleSocialClick = async (social) => {
        await trackCardInteraction(card.slug, 'social_click', social.platform);
        window.open(social.url, '_blank');
    };

    // Handle download vCard
    const handleDownloadVCard = async () => {
        await trackCardInteraction(card.slug, 'vcard_download');
        saveContactToDevice(card);
    };

    // Handle share
    const handleShare = async () => {
        await trackCardInteraction(card.slug, 'share_click');
        setShareOpen(true);
    };

    // Handle website click
    const handleWebsiteClick = async (website) => {
        await trackCardInteraction(card.slug, 'website_click', website.label);
        window.open(website.url, '_blank');
    };

    // Add WhatsApp click handler
    const handleWhatsAppClick = () => {
        // Always show the form for CorporateTheme
        setIsFormOpen(true);
    };

    const handleCloseForm = () => {
        setIsFormOpen(false);
        setPhoneNumber('');
        setError('');
    };

    const handleWhatsAppSubmit = async (e) => {
        e.preventDefault();
        
        // Basic validation
        const phoneRegex = /^\d{8,10}$/;
        if (!phoneRegex.test(phoneNumber)) {
            setError('Please enter a valid phone number (8-10 digits)');
            return;
        }
        
        // Clear any previous error
        setError('');
        
        // Prepare contact details message
        const contactDetails = `
Hi! Here are my contact details:
${card.name}
${card.designation || ''}
${card.company_name || ''}
${card.address || ''}
${card.contacts?.find(c => c.type === 'email')?.value || ''}
${card.contacts?.find(c => c.type === 'phone')?.value || ''}
${card.websites?.find(w => w.url)?.url || ''}
`.trim();
        
        // Format the WhatsApp URL
        const whatsappNumber = countryCode === '+65' ? '65' : '91';
        const encodedMessage = encodeURIComponent(contactDetails);
        const whatsappUrl = `https://wa.me/${whatsappNumber}${phoneNumber}?text=${encodedMessage}`;
        
        // Track interaction
        await trackCardInteraction(card.slug, 'whatsapp_click');
        
        // Reset form
        setPhoneNumber('');
        setIsFormOpen(false);
        
        // Open WhatsApp in a new tab
        window.open(whatsappUrl, '_blank');
    };

    // Combine all contact methods for the grid
    const getAllActions = () => {
        const actions = [];

        // Add contacts
        if (card.contacts) {
            card.contacts.forEach((contact, index) => {
                const style = getContactStyle(contact.type);
                actions.push({
                    type: 'contact',
                    key: `contact-${index}`,
                    icon: getContactIcon(contact.type),
                    color: style.color,
                    background: style.bg,
                    label: contact.type,
                    onClick: () => handleContactClick(contact),
                });
            });
        }

        // Add social profiles
        if (card.social_profiles) {
            card.social_profiles.forEach((social, index) => {
                const style = getSocialStyle(social.platform);
                actions.push({
                    type: 'social',
                    key: `social-${index}`,
                    icon: socialIcons[social.platform?.toLowerCase()] || <Language />,
                    color: style.color,
                    background: style.bg,
                    label: social.platform,
                    onClick: () => handleSocialClick(social),
                });
            });
        }

        // Add websites
        if (card.websites) {
            card.websites.forEach((website, index) => {
                actions.push({
                    type: 'website',
                    key: `website-${index}`,
                    icon: <Language />,
                    color: '#ffffff',
                    background: '#6366f1',
                    label: website.label,
                    onClick: () => handleWebsiteClick(website),
                });
            });
        }

        return actions;
    };

    const actions = getAllActions();

    // Only show buttons if not in preview or edit mode
    const shouldShowButtons = !isPreview && !isEditMode;

    return (
        <MobileContainer>
            <Box className="main-content">
                {/* Cover Photo and Logo Section */}
                {card.cover_photo_url && (
                    <CoverPhotoContainer
                        sx={{
                            backgroundImage: `url(${getImageUrl(card.cover_photo_url)})`,
                        }}
                    >
                        {/* Company Logo Overlay */}
                        {card.company_logo_url && (
                            <CompanyLogoOverlay>
                                <img
                                    src={getImageUrl(card.company_logo_url)}
                                    alt="Company Logo"
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        maxWidth: '120px',
                                        maxHeight: '60px',
                                        objectFit: 'contain'
                                    }}
                                />
                            </CompanyLogoOverlay>
                        )}
                    </CoverPhotoContainer>
                )}

                {/* Standalone Company Logo (when no cover photo) */}
                {card.company_logo_url && !card.cover_photo_url && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            pt: 4,
                            pb: 0,
                            background: '#fff',
                        }}
                    >
                        <Box
                            sx={{
                                width: 72,
                                height: 72,
                                borderRadius: '50%',
                                background: '#fff',
                                boxShadow: '0 2px 12px rgba(0,0,0,0.10)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mb: 3,
                            }}
                        >
                            <img
                                src={getImageUrl(card.company_logo_url)}
                                alt="Company Logo"
                                style={{ width: 48, height: 48, objectFit: 'contain' }}
                            />
                        </Box>
                    </Box>
                )}

                <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1, background: '#fff', pt: card.company_logo_url && !card.cover_photo_url ? 0 : 4 }}>
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, ease: "easeOut" }}
                    >
                        <ProfileCard hasProfilePicture={!!getImageUrl(card.profile_picture_url)}>
                            {/* Profile Picture - Only render if exists */}
                            {getImageUrl(card.profile_picture_url) && (
                                <Grow in={true} timeout={800}>
                                    <ProfileAvatar
                                        src={getImageUrl(card.profile_picture_url)}
                                        alt={card.name}
                                    />
                                </Grow>
                            )}

                            {/* Name and Title */}
                            <Fade in={true} timeout={1000}>
                                <Box sx={{ mt: !getImageUrl(card.profile_picture_url) ? 2 : 0 }}>
                                    <Typography
                                        variant="h4"
                                        sx={{
                                            fontWeight: 700,
                                            color: '#1a202c',
                                            mb: 1,
                                            fontSize: { xs: '1.4rem', md: '1.8rem' },
                                            letterSpacing: '-0.02em',
                                        }}
                                    >
                                        {card.name}
                                    </Typography>

                                    {card.designation && (
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                fontWeight: 500,
                                                color: '#4a5568',
                                                mb: 1,
                                                fontSize: { xs: '1rem', md: '1.1rem' },
                                            }}
                                        >
                                            {card.designation}
                                        </Typography>
                                    )}

                                    {/* Company Name */}
                                    {card.company_name && (
                                        <Typography
                                            variant="subtitle1"
                                            sx={{
                                                fontWeight: 600,
                                                color: '#2d3748',
                                                mb: 1,
                                                fontSize: { xs: '0.95rem', md: '1rem' },
                                            }}
                                        >
                                            {card.company_name}
                                        </Typography>
                                    )}

                                    {/* Address */}
                                    {card.address && (
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: '#718096',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: 0.5,
                                                mb: 2,
                                                fontSize: { xs: '0.9rem', md: '0.95rem' },
                                            }}
                                        >
                                            <LocationOn fontSize="small" />
                                            {card.address}
                                        </Typography>
                                    )}
                                </Box>
                            </Fade>

                            {/* Action Buttons Grid */}
                            {actions.length > 0 && (
                                <Box sx={{ mt: !getImageUrl(card.profile_picture_url) ? 1 : 3 }}>
                                    {Array.from({ length: Math.ceil(actions.length / 4) }, (_, rowIndex) => (
                                        <Grid 
                                            container 
                                            spacing={2} 
                                            key={rowIndex}
                                            sx={{ 
                                                mb: rowIndex < Math.ceil(actions.length / 4) - 1 ? 2 : 0,
                                                px: { xs: 1, sm: 2 }
                                            }}
                                        >
                                            {actions.slice(rowIndex * 4, (rowIndex + 1) * 4).map((action, index) => (
                                                <Grid 
                                                    item 
                                                    xs={3} 
                                                    key={action.key}
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        alignItems: 'center'
                                                    }}
                                                >
                                                    <Box sx={{ textAlign: 'center' }}>
                                                        <motion.div
                                                            initial={{ opacity: 0, scale: 0.8 }}
                                                            animate={{ opacity: 1, scale: 1 }}
                                                            transition={{
                                                                duration: 0.3,
                                                                delay: 0.1 * (rowIndex * 4 + index),
                                                                ease: "easeOut"
                                                            }}
                                                        >
                                                            <ActionButton
                                                                onClick={action.onClick}
                                                                sx={{
                                                                    backgroundColor: action.background,
                                                                    color: action.color,
                                                                    '&:hover': {
                                                                        backgroundColor: action.background,
                                                                        filter: 'brightness(1.1)',
                                                                    },
                                                                }}
                                                            >
                                                                {action.icon}
                                                            </ActionButton>
                                                        </motion.div>
                                                    </Box>
                                                </Grid>
                                            ))}
                                            {/* Fill empty cells in the last row if needed */}
                                            {rowIndex === Math.ceil(actions.length / 4) - 1 && 
                                             actions.length % 4 !== 0 && 
                                             Array.from({ length: 4 - (actions.length % 4) }, (_, emptyIndex) => (
                                                <Grid item xs={3} key={`empty-${emptyIndex}`}>
                                                    <Box sx={{ height: '60px' }} /> {/* Empty placeholder */}
                                                </Grid>
                                            ))}
                                        </Grid>
                                    ))}
                                </Box>
                            )}

                            {/* Floating Save Button is now outside ProfileCard */}
                        </ProfileCard>

                        {/* About Us Section */}
                        {(() => {
                            // Get about content from various possible sources (case-insensitive search)
                            let aboutContent = null;
                            let aboutFieldName = 'About Us'; // Default title
                            
                            // Check different sources for about content
                            if (card.bio) {
                                aboutContent = card.bio;
                            } else if (card.description) {
                                aboutContent = card.description;
                            } else if (card.about) {
                                aboutContent = card.about;
                            } else if (card.user?.about) {
                                aboutContent = card.user.about;
                            } else if (card.contents) {
                                // Find about content in contents array and get the field_name
                                const aboutContentItem = card.contents.find(c => 
                                    c.field_name?.toLowerCase() === 'about' || 
                                    c.field_name?.toLowerCase() === 'bio' || 
                                    c.field_name?.toLowerCase() === 'description'
                                );
                                if (aboutContentItem) {
                                    aboutContent = aboutContentItem.field_value;
                                    aboutFieldName = aboutContentItem.field_name; // Use actual field name from JSON
                                }
                            }
                            
                            // Helper function to strip HTML tags and convert to plain text
                            const stripHtml = (html) => {
                                if (!html) return '';
                                return html
                                    .replace(/<p>/g, '')
                                    .replace(/<\/p>/g, '\n')
                                    .replace(/<br\s*\/?>/g, '\n')
                                    .replace(/<[^>]*>/g, '')
                                    .replace(/\n\s*\n/g, '\n\n')
                                    .trim();
                            };
                            
                            return aboutContent && (
                                <motion.div
                                    initial={{ opacity: 0, y: 30 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: 0.8 }}
                                >
                                    <ContentSection hasProfilePicture={!!getImageUrl(card.profile_picture_url)}>
                                        <SectionTitle>
                                            <Info />
                                            {aboutFieldName}
                                        </SectionTitle>
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                color: '#4a5568',
                                                lineHeight: 1.7,
                                                fontSize: '0.9rem',
                                                textAlign: 'left',
                                                whiteSpace: 'pre-line',
                                            }}
                                        >
                                            {stripHtml(aboutContent)}
                                        </Typography>
                                    </ContentSection>
                                </motion.div>
                            );
                        })()}

                        {/* Achievements & Awards Section */}
                        {card.achievements && card.achievements.length > 0 && (
                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.9 }}
                            >
                                <ContentSection hasProfilePicture={!!getImageUrl(card.profile_picture_url)}>
                                    <SectionTitle>
                                        🏆 Achievements & Awards
                                    </SectionTitle>
                                    <Grid container spacing={3}>
                                        {card.achievements.map((achievement, index) => {
                                            console.log(`🔍 Processing achievement ${index}:`, achievement);
                                            console.log(`📁 File type for image_url:`, getFileExtension(achievement.image_url));
                                            
                                            return (
                                                <Grid item xs={12} sm={6} md={6} lg={4} key={achievement.id || index}>
                                                    <AchievementContainer>
                                                        {/* 1. Achievement Image */}
                                                        {achievement.image_url && isImageFile(achievement.image_url) && (
                                                            <AchievementImageWrapper>
                                                                <AchievementImage 
                                                                    src={getImageUrl(achievement.image_url)} 
                                                                    alt={achievement.caption || `Achievement ${index + 1}`}
                                                                    onLoad={() => {
                                                                        console.log('✅ Image loaded successfully:', getImageUrl(achievement.image_url));
                                                                    }}
                                                                    onError={(e) => {
                                                                        console.error('❌ Image load error for achievement:', achievement);
                                                                        e.target.src = 'https://via.placeholder.com/300x160?text=Image+Not+Found';
                                                                    }}
                                                                />
                                                            </AchievementImageWrapper>
                                                        )}
                                                        
                                                        {/* 2. Caption */}
                                                        {achievement.caption && (
                                                            <AchievementCaption component="h3">{achievement.caption}</AchievementCaption>
                                                        )}
                                                        
                                                        {/* 3. View Certificate Link */}
                                                        {achievement.certificate_link && (
                                                            <AchievementLinks>
                                                                <AchievementButton 
                                                                    href={achievement.certificate_link}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="certificate"
                                                                    startIcon={<EmojiEvents className="achievement-icon" />}
                                                                >
                                                                    View Certificate
                                                                </AchievementButton>
                                                            </AchievementLinks>
                                                        )}
                                                        
                                                        {/* 4. YouTube Video */}
                                                        {achievement.youtube_url && (
                                                            <MediaContainer>
                                                                <VideoContainer>
                                                                    {!playingVideos[achievement.id || index] ? (
                                                                        <Box onClick={() => toggleVideo(achievement.id || index)}>
                                                                            <Box
                                                                                component="img"
                                                                                src={`https://img.youtube.com/vi/${getYouTubeVideoId(achievement.youtube_url)}/maxresdefault.jpg`}
                                                                                alt={achievement.caption || `Achievement ${index + 1}`}
                                                                                sx={{
                                                                                    position: 'absolute',
                                                                                    top: 0,
                                                                                    left: 0,
                                                                                    width: '100%',
                                                                                    height: '100%',
                                                                                    objectFit: 'cover'
                                                                                }}
                                                                                onError={(e) => {
                                                                                    e.target.src = `https://img.youtube.com/vi/${getYouTubeVideoId(achievement.youtube_url)}/hqdefault.jpg`;
                                                                                    e.target.onerror = () => {
                                                                                        e.target.src = 'https://via.placeholder.com/300x180?text=Video+Thumbnail+Not+Found';
                                                                                    };
                                                                                }}
                                                                            />
                                                                            <VideoOverlay>
                                                                                <PlayArrow 
                                                                                    className="play-icon"
                                                                                    sx={{
                                                                                        fontSize: '36px',
                                                                                        color: 'white',
                                                                                        opacity: 0.9,
                                                                                        transition: 'all 0.3s ease'
                                                                                    }}
                                                                                />
                                                                            </VideoOverlay>
                                                                        </Box>
                                                                    ) : (
                                                                        <YouTubeEmbed
                                                                            src={getYouTubeEmbedUrl(achievement.youtube_url)}
                                                                            title={achievement.caption || `Achievement ${index + 1}`}
                                                                            frameBorder="0"
                                                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                                                            allowFullScreen
                                                                            referrerPolicy="strict-origin-when-cross-origin"
                                                                        />
                                                                    )}
                                                                </VideoContainer>
                                                            </MediaContainer>
                                                        )}
                                                        
                                                        {/* 5. Uploaded Video (from video_url or image_url if it's a video) */}
                                                        {((achievement.video_url) || (achievement.image_url && isVideoFile(achievement.image_url))) && (
                                                            <MediaContainer>
                                                                <UploadedVideoPlayer 
                                                                    src={getImageUrl(achievement.video_url || achievement.image_url)}
                                                                    controls
                                                                    preload="metadata"
                                                                    onError={(e) => {
                                                                        console.error('❌ Video load error:', e);
                                                                    }}
                                                                >
                                                                    Your browser does not support the video tag.
                                                                </UploadedVideoPlayer>
                                                            </MediaContainer>
                                                        )}
                                                        
                                                        {/* 6. PDF with Blue Color */}
                                                        {achievement.pdf_url && (
                                                            <AchievementLinks>
                                                                <AchievementButton 
                                                                    href={getImageUrl(achievement.pdf_url)}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="pdf"
                                                                    startIcon={<PictureAsPdf className="achievement-icon" />}
                                                                >
                                                                    View PDF
                                                                </AchievementButton>
                                                            </AchievementLinks>
                                                        )}
                                                    </AchievementContainer>
                                                </Grid>
                                            );
                                        })}
                                    </Grid>
                                </ContentSection>
                            </motion.div>
                        )}

                        {/* Services Section */}
                        {(() => {
                            // Get services content from various possible sources (case-insensitive search)
                            let servicesContent = null;
                            let servicesFieldName = 'Our Services'; // Default title
                            
                            // Check different sources for services content
                            if (card.services) {
                                servicesContent = card.services;
                            } else if (card.user?.services) {
                                servicesContent = card.user.services;
                            } else if (card.contents) {
                                // Find services content in contents array and get the field_name
                                const servicesContentItem = card.contents.find(c => 
                                    c.field_name?.toLowerCase() === 'services' || 
                                    c.field_name?.toLowerCase() === 'service'
                                );
                                if (servicesContentItem) {
                                    servicesContent = servicesContentItem.field_value;
                                    servicesFieldName = servicesContentItem.field_name; // Use actual field name from JSON
                                }
                            }

                            // Helper function to strip HTML tags and convert to plain text
                            const stripHtml = (html) => {
                                if (!html) return '';
                                return html
                                    .replace(/<p>/g, '')
                                    .replace(/<\/p>/g, '\n')
                                    .replace(/<br\s*\/?>/g, '\n')
                                    .replace(/<[^>]*>/g, '')
                                    .replace(/\n\s*\n/g, '\n\n')
                                    .trim();
                            };

                            // If servicesContent is a string (like HTML content), handle it appropriately
                            if (typeof servicesContent === 'string') {
                                try {
                                    // First try to parse as JSON
                                    servicesContent = JSON.parse(servicesContent);
                                } catch (e) {
                                    // If parsing fails, it's likely HTML content - create a single service card
                                    const cleanedContent = stripHtml(servicesContent);
                                    if (cleanedContent) {
                                        servicesContent = [{ 
                                            title: servicesFieldName, 
                                            description: cleanedContent 
                                        }];
                                    } else {
                                        servicesContent = null;
                                    }
                                }
                            }

                            // Ensure servicesContent is an array with valid content
                            if (!Array.isArray(servicesContent) || servicesContent.length === 0) {
                                return null;
                            }
                            
                            return (
                                <motion.div
                                    initial={{ opacity: 0, y: 30 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: 1.0 }}
                                >
                                    <ContentSection hasProfilePicture={!!getImageUrl(card.profile_picture_url)}>
                                        <SectionTitle>
                                            <Work />
                                            {servicesFieldName}
                                        </SectionTitle>
                                        <Grid container spacing={2}>
                                            {servicesContent.map((service, index) => (
                                                <Grid item xs={12} key={index}>
                                                    <Box
                                                        sx={{
                                                            p: 3,
                                                            border: '1px solid #e2e8f0',
                                                            borderRadius: '12px',
                                                            background: 'linear-gradient(135deg, #f8faff 0%, #f0f4ff 100%)',
                                                            transition: 'all 0.3s ease',
                                                            '&:hover': {
                                                                borderColor: '#667eea',
                                                                transform: 'translateY(-2px)',
                                                                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.15)',
                                                            },
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="h6"
                                                            sx={{
                                                                color: '#1a202c',
                                                                fontWeight: 600,
                                                                fontSize: '1.25rem',
                                                                mb: 2,
                                                            }}
                                                        >
                                                            {service.title || service.name || (typeof service === 'string' && service)}
                                                        </Typography>
                                                        {(service.description || service.desc) && (
                                                            <Typography
                                                                variant="body1"
                                                                sx={{
                                                                    color: '#4a5568',
                                                                    lineHeight: 1.7,
                                                                    whiteSpace: 'pre-line',
                                                                }}
                                                            >
                                                                {stripHtml(service.description || service.desc)}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </ContentSection>
                                </motion.div>
                            );
                        })()}

                        {/* Custom Content Sections */}
                        {card.contents && card.contents.map((content, index) => {
                            // Skip if this content has already been rendered in About or Services sections
                            const isAboutContent = content.field_name?.toLowerCase() === 'about' || 
                                                content.field_name?.toLowerCase() === 'bio' || 
                                                content.field_name?.toLowerCase() === 'description';
                            const isServiceContent = content.field_name?.toLowerCase() === 'services' || 
                                                   content.field_name?.toLowerCase() === 'service';
                            const isAchievementContent = content.field_name?.toLowerCase() === 'achievements' || 
                                                   content.field_name?.toLowerCase() === 'achievement';
                            
                            if (isAboutContent || isServiceContent || isAchievementContent) return null;

                            // Helper function to strip HTML tags and convert to plain text
                            const stripHtml = (html) => {
                                if (!html) return '';
                                return html
                                    .replace(/<p>/g, '')
                                    .replace(/<\/p>/g, '\n')
                                    .replace(/<br\s*\/?>/g, '\n')
                                    .replace(/<[^>]*>/g, '')
                                    .replace(/\n\s*\n/g, '\n\n')
                                    .trim();
                            };

                            return (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 30 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: 1.2 + (index * 0.1) }}
                                >
                                    <ContentSection hasProfilePicture={!!getImageUrl(card.profile_picture_url)}>
                                        <SectionTitle>
                                            <Info />
                                            {content.field_name}
                                        </SectionTitle>
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                color: '#4a5568',
                                                lineHeight: 1.7,
                                                fontSize: '1rem',
                                                textAlign: 'left',
                                                whiteSpace: 'pre-line',
                                            }}
                                        >
                                            {stripHtml(content.field_value)}
                                        </Typography>
                                    </ContentSection>
                                </motion.div>
                            );
                        })}

                        {/* Batches Section */}
                        {card.batches && card.batches.length > 0 && (
                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 1.0 }}
                            >
                                <ContentSection hasProfilePicture={!!getImageUrl(card.profile_picture_url)}>
                                    <SectionTitle>
                                        🎓 Batches
                                    </SectionTitle>
                                    <Grid container spacing={2}>
                                        {card.batches.map((batch, index) => (
                                            <Grid item xs={12} sm={6} md={4} key={batch.id || index}>
                                                <Box
                                                    sx={{
                                                        background: 'rgba(255, 255, 255, 0.9)',
                                                        borderRadius: '16px',
                                                        padding: 2,
                                                        textAlign: 'center',
                                                        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                                                        transition: 'all 0.3s ease',
                                                        '&:hover': {
                                                            transform: 'translateY(-4px)',
                                                            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
                                                        },
                                                    }}
                                                >
                                                    {batch.image_url && (
                                                        <Box sx={{ mb: 2 }}>
                                                            <img
                                                                src={batch.image_url}
                                                                alt={batch.caption || 'Batch'}
                                                                style={{
                                                                    width: '60px',
                                                                    height: '60px',
                                                                    borderRadius: '12px',
                                                                    objectFit: 'cover',
                                                                    margin: '0 auto',
                                                                }}
                                                            />
                                                        </Box>
                                                    )}
                                                    {batch.caption && (
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                fontWeight: 600,
                                                                color: '#374151',
                                                                mb: 0.5,
                                                                fontSize: { xs: '0.75rem', sm: '0.85rem' },
                                                                lineHeight: 1.3,
                                                            }}
                                                        >
                                                            {batch.caption}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </ContentSection>
                            </motion.div>
                        )}

                        {/* Gallery Section */}
                        {card.product_images && card.product_images.length > 0 && (
                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 1.2 }}
                            >
                                <ContentSection hasProfilePicture={!!getImageUrl(card.profile_picture_url)}>
                                    <SectionTitle>
                                        <PhotoLibrary />
                                        Gallery
                                    </SectionTitle>
                                    <Grid container spacing={2}>
                                        {card.product_images.map((image, index) => (
                                            <Grid item xs={12} sm={6} md={4} key={index}>
                                                <Box
                                                    sx={{
                                                        position: 'relative',
                                                        borderRadius: '12px',
                                                        overflow: 'hidden',
                                                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                                                        transition: 'all 0.3s ease',
                                                        '&:hover': {
                                                            transform: 'translateY(-4px)',
                                                            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
                                                        },
                                                    }}
                                                >
                                                    <img
                                                        src={getImageUrl(image.url)}
                                                        alt={image.caption || `Gallery image ${index + 1}`}
                                                        style={{
                                                            width: '100%',
                                                            height: '200px',
                                                            objectFit: 'cover',
                                                            display: 'block',
                                                        }}
                                                    />
                                                    {image.caption && (
                                                        <Box
                                                            sx={{
                                                                position: 'absolute',
                                                                bottom: 0,
                                                                left: 0,
                                                                right: 0,
                                                                background: 'linear-gradient(transparent, rgba(0, 0, 0, 0.7))',
                                                                color: 'white',
                                                                p: 2,
                                                            }}
                                                        >
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    fontSize: '0.875rem',
                                                                    fontWeight: 500,
                                                                }}
                                                            >
                                                                {image.caption}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </Box>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </ContentSection>
                            </motion.div>
                        )}
                    </motion.div>
                </Container>

                {/* Floating Save Contact & WhatsApp Buttons */}
                {shouldShowButtons && (
                    <>
                        <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.6, delay: 1.2 }}
                        >
                            <Stack 
                                direction="row" 
                                spacing={2} 
                                sx={{ 
                                    position: 'fixed',
                                    bottom: { xs: 16, sm: 24 },
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    zIndex: 1000,
                                    width: 'auto',
                                    maxWidth: '90%'
                                }}
                            >
                                <FloatingActionButton
                                    onClick={handleDownloadVCard}
                                    startIcon={<Download />}
                                >
                                    Save Contact
                                </FloatingActionButton>

                                <WhatsAppButton
                                    onClick={handleWhatsAppClick}
                                    startIcon={<WhatsApp />}
                                >
                                    WhatsApp
                                </WhatsAppButton>
                            </Stack>
                        </motion.div>

                        {/* WhatsApp Form Dialog */}
                        <Dialog
                            open={isFormOpen}
                            onClose={handleCloseForm}
                            maxWidth="xs"
                            fullWidth
                            PaperProps={{
                                sx: {
                                    borderRadius: 2,
                                    p: 2
                                }
                            }}
                        >
                            <DialogTitle sx={{ pb: 1 }}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <WhatsApp sx={{ color: '#25D366' }} />
                                    <Typography variant="h6">Send WhatsApp Message</Typography>
                                </Stack>
                            </DialogTitle>
                            <DialogContent>
                                <Stack spacing={2} sx={{ mt: 1 }}>
                                    <FormControl fullWidth>
                                        <Select
                                            value={countryCode}
                                            onChange={(e) => setCountryCode(e.target.value)}
                                            variant="outlined"
                                        >
                                            <MenuItem value="+65">🇸🇬 Singapore (+65)</MenuItem>
                                            <MenuItem value="+91">🇮🇳 India (+91)</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <TextField
                                        fullWidth
                                        label="Phone Number"
                                        variant="outlined"
                                        type="tel"
                                        value={phoneNumber}
                                        onChange={(e) => setPhoneNumber(e.target.value)}
                                        error={!!error}
                                        helperText={error}
                                    />
                                </Stack>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={handleCloseForm}>Cancel</Button>
                                <Button
                                    onClick={handleWhatsAppSubmit}
                                    variant="contained"
                                    sx={{
                                        background: 'linear-gradient(135deg, #25D366, #128C7E)',
                                        '&:hover': {
                                            background: 'linear-gradient(135deg, #128C7E, #075E54)',
                                        }
                                    }}
                                >
                                    Send Message
                                </Button>
                            </DialogActions>
                        </Dialog>
                    </>
                )}

                {/* Share Modal */}
                <CardShare 
                    open={shareOpen} 
                    onClose={() => setShareOpen(false)} 
                    card={card} 
                />

                {/* Footer */}
                <Box
                  sx={{
                    width: '100%',
                    py: 2,
                    mt: 'auto',
                    mb: { xs: '20px', sm: '100px' }, // Add margin bottom for floating buttons
                    borderTop: '1px solid rgba(0,0,0,0.1)',
                    textAlign: 'center',
                    bgcolor: 'rgba(0,0,0,0.02)'
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.75rem'
                    }}
                  >
                    Powered By Powersoft Techno Solution Pte Ltd
                  </Typography>
                </Box>
            </Box>
        </MobileContainer>
    );
};

export default CorporateTheme; 